import type { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';

export interface PriceChangeDay {
	date: string;
	pointIndex: number;
	submissions: PriceChangeSubmission[];
}

export interface PriceChangeRoomSummary {
	roomId: number;
	name: string;
	previous: number | null;
	latest: number;
}

export const summarizePriceChangeDay = (day: PriceChangeDay): { rows: PriceChangeRoomSummary[]; resendCount: number } => {
	const rooms = new Map<number, PriceChangeRoomSummary>();
	for (const submission of [...day.submissions].reverse()) {
		for (const item of submission.items) {
			const previous = rooms.get(item.pricingArchitectureRoomId);
			rooms.set(item.pricingArchitectureRoomId, {
				roomId: item.pricingArchitectureRoomId,
				name: item.roomName,
				previous: previous ? previous.previous : item.previousPrice,
				latest: item.price,
			});
		}
	}
	const values = [...rooms.values()];
	const isResend = (room: PriceChangeRoomSummary) => room.previous !== null &&
		Math.round(room.previous * 100) === Math.round(room.latest * 100);
	return { rows: values.filter(room => !isResend(room)), resendCount: values.filter(isResend).length };
};

const localDay = (utcDateTime: string) => {
	const date = new Date(utcDateTime.endsWith('Z') ? utcDateTime : `${utcDateTime}Z`);
	return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
};

export const placePriceChangeDays = (pointDates: string[], submissions: PriceChangeSubmission[]): PriceChangeDay[] => {
	if (pointDates.length === 0) return [];
	const pointDays = pointDates.map(localDay);
	const days = new Map<string, PriceChangeSubmission[]>();
	for (const submission of submissions) {
		const day = localDay(submission.finishDate);
		days.set(day, [...(days.get(day) ?? []), submission]);
	}
	return [...days.entries()].sort(([left], [right]) => right.localeCompare(left)).flatMap(([date, daySubmissions]) => {
		if (date < pointDays[0]) return [];
		const firstAtOrAfter = pointDays.findIndex(pointDay => pointDay >= date);
		const pointIndex = firstAtOrAfter < 0 ? pointDays.length - 1 : firstAtOrAfter;
		return [{ date, pointIndex, submissions: daySubmissions.sort((left, right) => right.finishDate.localeCompare(left.finishDate)) }];
	});
};
